import { type FetchQueryFetchPolicy } from 'relay-runtime';

export type QueryInteropFetchPolicy =
	| 'cache-first'
	| 'cache-and-network'
	| 'network-only'
	| 'cache-only'
	| 'no-cache'
	/**
	 * Network-only **and never writes to the Relay store**. Apollo's `'no-cache'` has these
	 * semantics; the Relay branch of `'no-cache'` does not (it maps to `'network-only'`, which
	 * normalizes), so this value exists to express the missing half.
	 *
	 * Only reach for this when a *single response* contains two objects that share an `id` but
	 * represent different things, because normalization then collapses them and the later write
	 * silently wins. No fetch policy other than this one can avoid that: the corruption happens
	 * inside one normalized write, so even a cold store is corrupted by the first response.
	 *
	 * Cost: these operations no longer read from or react to the store, so no fragments, no
	 * `usePaginationFragment`, and no SSR hydration for them. Do not use it for freshness — plain
	 * `'network-only'` already covers that and keeps the store.
	 *
	 * @deprecated Temporary. Introduced for `@confluence/content-history-new`, where
	 * `Version.content` and `content(id:, version:)` return the version-agnostic *page* id, so
	 * current and historical content normalize to one record and the diff renders "no changes".
	 * Remove every usage — and this value — once version-scoped reads return revision-scoped ids.
	 */
	| 'no-store'
	| 'standby';

// Relay:  https://relay.dev/docs/guided-tour/reusing-cached-data/fetch-policies/
// Apollo: https://www.apollographql.com/docs/react/data/queries#supported-fetch-policies
export function mapFetchPolicyForQuery(
	fetchPolicy: QueryInteropFetchPolicy | undefined,
): FetchQueryFetchPolicy {
	switch (fetchPolicy) {
		case 'no-cache':
		case 'network-only':
		case 'cache-and-network':
		// 'no-store' additionally suppresses the store write, which a Relay fetch policy cannot
		// express — `isNoStoreFetchPolicy` drives that separately. The policy itself only has to
		// guarantee the network round trip.
		case 'no-store':
			// Map to 'network-only' to preserve the mandatory network refetch.
			// The cached-data-first behaviour can't be expressed here.
			return 'network-only';
		case 'cache-first':
		case 'cache-only': // TODO: shim a true store-only read; defaults to 'store-or-network' for now
		case 'standby': // No direct Relay equivalent — defaults to 'store-or-network'
		default:
			return 'store-or-network';
	}
}

/**
 * Whether this policy must bypass the Relay store entirely — no writes, no reads, no subscription.
 *
 * Kept next to {@link mapFetchPolicyForQuery} so both halves of `'no-store'` are defined in one
 * place: the Relay fetch policy it maps to, and the store suppression it additionally implies.
 */
export function isNoStoreFetchPolicy(fetchPolicy: QueryInteropFetchPolicy | undefined): boolean {
	return fetchPolicy === 'no-store';
}
